/**
 * Tenant branding values — the validation rules and the colour arithmetic that
 * every surface rendering an operator-chosen colour needs.
 *
 * One copy, three consumers: the helpdesk backend (the authoritative gate on
 * the read and write paths), the helpdesk settings UI (the same rules mirrored
 * beside the field so the operator is told at typing time, not from a 422),
 * and the knowledgebase portal (which paints a brand colour on its header and
 * needs the same readable-foreground answer the chat widget gives, or the two
 * disagree side by side on one site).
 *
 * The live-chat widget is the fourth consumer, by a different route: its bundle
 * ships to third-party origins and takes no workspace package dependency, so
 * its build aliases `@airclou/utils/src/branding` straight to this source file
 * and inlines it (`apps/helpdesk/widget/vite.config.ts`). That is why this file
 * imports nothing: anything it pulled in would ride along into the widget.
 */
/**
 * Opaque hex only — `#rgb` or `#rrggbb`.
 *
 * Deliberately narrower than CSS allows. Every richer syntax CSS accepts
 * (`rgb()`, `var()`, `color-mix()`) brings parentheses with it — the character
 * class that makes a custom property interesting to attack. Nothing legitimate
 * is lost by refusing them: the admin UI writes these from a colour field.
 *
 * No alpha forms (`#rgba`, `#rrggbbaa`): nothing produces them (a colour field
 * emits `#rrggbb`), and every surface paints these over opaque chrome, so a
 * translucent value could only ever mean ghosted text or a see-through header.
 * The contrast arithmetic below treats colours as opaque; refusing alpha at the
 * gate is what keeps that assumption true.
 */
export const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
/**
 * Font stacks are the one free-text branding field, so they get a character
 * allowlist rather than a pattern: letters and digits in any script (a zh
 * tenant names `微软雅黑` next to `"Microsoft YaHei"`), spaces, and the
 * punctuation a stack legitimately contains (`Helvetica Neue, "Segoe UI",
 * sans-serif`). What it excludes is the point — `;` `{` `}` `(` `)` `<` `>`
 * `\` and `/` are every character needed to close a declaration and open
 * something else.
 */
export const SAFE_FONT_FAMILY_PATTERN = /^[\p{L}\p{N} ,._'"-]+$/u;
/**
 * The allowlist above says which characters may appear; this says how they
 * may be arranged: one or more families separated by single commas, each
 * either a quoted string or an unquoted run of words. It exists because a
 * value can pass the allowlist and still be broken CSS, and broken CSS does
 * not fail loudly — it fails somewhere else. An unbalanced quote (`"Segoe UI`)
 * in the email wrapper's inline style swallows every declaration after it up
 * to the end of the attribute, so the body text colour silently goes; a
 * trailing comma (`Inter,`) makes the widget's `font-family: var(--hdw-font)`
 * invalid at computed-value time, so the widget inherits the host page's font
 * instead of either the brand's or its own fallback.
 */
const FONT_FAMILY_ENTRY = String.raw `(?:"[^"]+"|'[^']+'|[\p{L}\p{N}_.-]+(?: +[\p{L}\p{N}_.-]+)*)`;
export const FONT_FAMILY_LIST_PATTERN = new RegExp(`^${FONT_FAMILY_ENTRY}(?: *, *${FONT_FAMILY_ENTRY})*$`, 'u');
const FIRST_FONT_FAMILY_ENTRY = new RegExp(`^${FONT_FAMILY_ENTRY}`, 'u');
export const MAX_FONT_FAMILY_LENGTH = 120;
export const MAX_URL_LENGTH = 2048;
/** Dark ink used when white would be unreadable on a brand colour. Matches the
 *  chat widget's own body text colour. */
export const BRANDING_DARK_INK = '#111827';
export const BRANDING_WHITE = '#ffffff';
/** WCAG AA for normal text. */
const MIN_TEXT_CONTRAST = 4.5;
/** A hash-less `2E5CFF` is accepted and returned as `#2E5CFF`: the portal's
 *  older read path allowed the bare form, so a stored value may lack the hash,
 *  and a typed one often does. Normalising here means every consumer — CSS
 *  variables, the email wrapper, the colour swatch — sees one shape. */
export function sanitizeCssColor(value) {
    if (typeof value !== 'string')
        return undefined;
    const trimmed = value.trim();
    const candidate = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    return HEX_COLOR_PATTERN.test(candidate) ? candidate : undefined;
}
/** A value is used as a whole or dropped as a whole — there is no stripping,
 *  since a half-sanitised font stack is a value nobody chose. */
export function sanitizeFontFamily(value) {
    if (typeof value !== 'string')
        return undefined;
    const trimmed = value.trim();
    if (!trimmed || trimmed.length > MAX_FONT_FAMILY_LENGTH)
        return undefined;
    if (!SAFE_FONT_FAMILY_PATTERN.test(trimmed))
        return undefined;
    return FONT_FAMILY_LIST_PATTERN.test(trimmed) ? trimmed : undefined;
}
/**
 * CSS generic families and the keywords that stand in for one. None of these
 * names a downloadable font, so a stylesheet request for them is a wasted
 * round trip that answers 400.
 */
const GENERIC_FONT_FAMILIES = new Set([
    'serif', 'sans-serif', 'monospace', 'cursive', 'fantasy', 'system-ui',
    'ui-serif', 'ui-sans-serif', 'ui-monospace', 'ui-rounded', 'emoji', 'math',
    'fangsong', 'inherit', 'initial', 'unset', 'revert',
]);
/**
 * Fonts that ship with the operating systems visitors use, so a stack that
 * leads with one is asking for what is already installed. Google Fonts has
 * none of these: the request answers 400 on every pageview and loads nothing.
 * Lower-cased for the lookup. Deliberately not exhaustive — a name missing
 * from here costs one failed request, not a wrong font — and it leaves out
 * families that are both a system font and a downloadable one (Roboto, Noto),
 * which still deserve the request on platforms that lack them.
 */
const SYSTEM_FONT_FAMILIES = new Set([
    '-apple-system', 'blinkmacsystemfont', 'segoe ui', 'arial', 'helvetica',
    'helvetica neue', 'times new roman', 'times', 'georgia', 'verdana', 'tahoma',
    'trebuchet ms', 'courier new', 'courier', 'impact', 'palatino',
    'palatino linotype', 'book antiqua', 'garamond', 'lucida grande',
    'lucida sans unicode', 'lucida console', 'gill sans', 'optima', 'candara',
    'calibri', 'cambria', 'consolas', 'menlo', 'monaco', 'microsoft yahei',
    'pingfang sc', 'hiragino sans', 'hiragino kaku gothic pro', 'meiryo',
    'malgun gothic', 'apple color emoji', 'segoe ui emoji',
]);
/**
 * The one family a web-font request should ask for: the FIRST entry of a
 * stack, unquoted — or undefined when that entry is a generic family or a
 * font the visitor's operating system already provides.
 *
 * A stack is a preference list, and the fallbacks in it are there precisely
 * because they are already installed. Asking a font service for the whole
 * stack (`"Segoe UI", Arial, sans-serif` URL-encoded into one `family=`) is a
 * request for a font called that, which no service has: Google Fonts answers
 * 400 and the brand font never loads. Asking for just `Segoe UI` is the same
 * 400 for a different reason — it is nobody's downloadable font — so the
 * common system names are skipped too. Expects a value `sanitizeFontFamily`
 * already accepted; anything else yields undefined.
 */
export function primaryFontFamily(stack) {
    const clean = sanitizeFontFamily(stack);
    if (!clean)
        return undefined;
    // Match the first entry rather than splitting on ',' — a quoted family may
    // itself contain a comma, and splitting would hand back half its name.
    const first = clean.match(FIRST_FONT_FAMILY_ENTRY)?.[0].replace(/^["']|["']$/g, '').trim();
    if (!first)
        return undefined;
    const key = first.toLowerCase();
    if (GENERIC_FONT_FAMILIES.has(key) || SYSTEM_FONT_FAMILIES.has(key))
        return undefined;
    return first;
}
/**
 * Characters `URL` accepts by REWRITING rather than refusing, which matters
 * because this function returns the value as typed rather than the parser's
 * `href`: ASCII tab and newline are silently deleted before parsing, a
 * backslash becomes `/` for http(s), and space `<` `>` `"` are percent-encoded
 * into `href`. Every one of them would therefore pass validation and be stored
 * verbatim, leaving the checked address and the stored address different
 * strings — and a stored space is what turns `<img src={{logo_url}}>` in an
 * operator's email wrapper, written without quotes, into an extra attribute.
 * None is legal in a URL unencoded (RFC 3986), so refusing is not a
 * restriction anyone can feel; the alternative — returning `parsed.href` —
 * would hand back an address the operator never typed and re-dirty every
 * settings form on load.
 */
const URL_REWRITTEN_CHARS = /[\s"<>\\]|[\u0000-\u001f\u007f]/;
/**
 * http/https only. `URL` alone is not the check: `javascript:alert(1)` parses
 * as a perfectly valid URL. The protocol test is the part that matters.
 */
export function sanitizeHttpUrl(value) {
    if (typeof value !== 'string')
        return undefined;
    const trimmed = value.trim();
    if (!trimmed || trimmed.length > MAX_URL_LENGTH)
        return undefined;
    if (URL_REWRITTEN_CHARS.test(trimmed))
        return undefined;
    let parsed;
    try {
        parsed = new URL(trimmed);
    }
    catch {
        return undefined;
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:')
        return undefined;
    return trimmed;
}
/**
 * Keep the branding fields that survive their own rule; `undefined` when none
 * do — which callers can treat as "no branding", because visually it is.
 *
 * A field is dropped, never repaired: a brand with a good colour and a broken
 * logo URL keeps its colour, and the missing one leaves whatever default the
 * surface already had. A substituted value would be a silent lie about what
 * the brand configured.
 *
 * Every value is untrusted here, including the ones that came out of our own
 * table — the two callers are the server gate building the visitor payload
 * (columns an admin can set are not trusted input just because a form usually
 * fills them) and the widget, which re-runs it over branding read back from
 * localStorage on the CUSTOMER's origin, where their scripts can write. Hence
 * `unknown` in rather than a typed shape: the whole point is that the caller
 * cannot vouch for it.
 */
export function sanitizeBrandingFields(input) {
    const fields = {};
    const primaryColor = sanitizeCssColor(input.primaryColor);
    if (primaryColor)
        fields.primaryColor = primaryColor;
    const accentColor = sanitizeCssColor(input.accentColor);
    if (accentColor)
        fields.accentColor = accentColor;
    const launcherIconUrl = sanitizeHttpUrl(input.launcherIconUrl);
    if (launcherIconUrl)
        fields.launcherIconUrl = launcherIconUrl;
    const headerLogoUrl = sanitizeHttpUrl(input.headerLogoUrl);
    if (headerLogoUrl)
        fields.headerLogoUrl = headerLogoUrl;
    const fontFamily = sanitizeFontFamily(input.fontFamily);
    if (fontFamily)
        fields.fontFamily = fontFamily;
    return Object.keys(fields).length > 0 ? fields : undefined;
}
/** Expand `#rgb` / `#rrggbb` to 0-255 channels, or null for anything else. */
export function hexToRgb(hex) {
    const value = hex.trim();
    if (!HEX_COLOR_PATTERN.test(value))
        return null;
    const body = value.slice(1);
    const expand = (s) => parseInt(s + s, 16);
    if (body.length === 3) {
        return { r: expand(body[0]), g: expand(body[1]), b: expand(body[2]) };
    }
    return {
        r: parseInt(body.slice(0, 2), 16),
        g: parseInt(body.slice(2, 4), 16),
        b: parseInt(body.slice(4, 6), 16),
    };
}
export function rgbToHex({ r, g, b }) {
    const channel = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
    return `#${channel(r)}${channel(g)}${channel(b)}`;
}
/** WCAG relative luminance. */
export function relativeLuminance({ r, g, b }) {
    const channel = (v) => {
        const s = v / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
/** WCAG contrast ratio between two luminances, always >= 1. */
export function contrastRatio(a, b) {
    const [hi, lo] = a > b ? [a, b] : [b, a];
    return (hi + 0.05) / (lo + 0.05);
}
/**
 * Pick a foreground that stays readable ON a brand's own colour: whichever of
 * white or the dark ink contrasts better. A legibility floor, not a design
 * opinion — on any reasonably dark brand colour white still wins.
 *
 * Returns undefined for anything that is not a hex colour rather than guessing:
 * `parseInt` of a non-hex pair is NaN, every comparison against NaN is false,
 * and a naive version would confidently return white — the exact wrong answer
 * on the pale colours this exists for.
 */
export function readableForeground(hexColor) {
    if (!hexColor)
        return undefined;
    const rgb = hexToRgb(hexColor);
    if (!rgb)
        return undefined;
    const bg = relativeLuminance(rgb);
    const onWhite = contrastRatio(bg, relativeLuminance(hexToRgb(BRANDING_WHITE)));
    const onDark = contrastRatio(bg, relativeLuminance(hexToRgb(BRANDING_DARK_INK)));
    return onDark > onWhite ? BRANDING_DARK_INK : BRANDING_WHITE;
}
/** Mix a colour toward black by `amount` (0-1). Used for hover states, where
 *  the convention is "the same colour, a little darker". */
export function darkenHex(hexColor, amount = 0.15) {
    const rgb = hexToRgb(hexColor);
    if (!rgb)
        return undefined;
    const k = 1 - Math.max(0, Math.min(1, amount));
    return rgbToHex({ r: rgb.r * k, g: rgb.g * k, b: rgb.b * k });
}
/** Mix a colour toward white by `amount` (0-1). The dark-mode counterpart of
 *  `darkenHex`: on a dark page a hover should go lighter, not darker. */
export function lightenHex(hexColor, amount = 0.15) {
    const rgb = hexToRgb(hexColor);
    if (!rgb)
        return undefined;
    const k = Math.max(0, Math.min(1, amount));
    return rgbToHex({ r: rgb.r + (255 - rgb.r) * k, g: rgb.g + (255 - rgb.g) * k, b: rgb.b + (255 - rgb.b) * k });
}
/** Smallest luminance ratio between a fill and its hover that reads as a change. */
const MIN_HOVER_STEP = 1.03;
/**
 * The dark-mode hover shade for a brand fill carrying `ink` as its label.
 * Hover on a dark page should go lighter, but lightening a fill with a white
 * label lowers its contrast — `#2E5CFF` with white is 5.1:1 at rest and 4.0:1
 * after a full `lightenHex`. So this lightens as far as it can (up to the
 * usual 15%) while the label stays at 4.5:1, or at its resting contrast when
 * the rest is already below that. The step must also be visible — a
 * luminance ratio of at least 1.03 against the rest, which a near-white brand
 * can't reach by lightening. If no lightening qualifies, it darkens instead: a
 * readable label and a visible hover beat the hover direction.
 */
export function darkModeHoverHex(hexColor, ink) {
    const rgb = hexToRgb(hexColor);
    const inkRgb = hexToRgb(ink);
    if (!rgb || !inkRgb)
        return undefined;
    const restL = relativeLuminance(rgb);
    const inkL = relativeLuminance(inkRgb);
    const floor = Math.min(MIN_TEXT_CONTRAST, contrastRatio(restL, inkL));
    for (let pct = 15; pct >= 3; pct -= 1) {
        const candidateL = relativeLuminance(hexToRgb(lightenHex(hexColor, pct / 100)));
        if (contrastRatio(candidateL, inkL) >= floor && contrastRatio(candidateL, restL) >= MIN_HOVER_STEP) {
            return lightenHex(hexColor, pct / 100);
        }
    }
    return darkenHex(hexColor);
}
/**
 * A brand colour that is safe to use as TEXT on a white surface.
 *
 * Returns the colour unchanged when it already clears WCAG AA against white,
 * so a brand that picked a sensible colour sees exactly what it picked.
 * Otherwise it is darkened in steps until it does — a pale yellow becomes an
 * olive rather than vanishing. Falls back to the dark ink if darkening somehow
 * never gets there (it always does within the step budget; the fallback is
 * there so the function can never return undefined for a valid colour).
 */
export function readableOnWhite(hexColor) {
    if (!hexColor)
        return undefined;
    const rgb = hexToRgb(hexColor);
    if (!rgb)
        return undefined;
    const whiteL = relativeLuminance(hexToRgb(BRANDING_WHITE));
    if (contrastRatio(relativeLuminance(rgb), whiteL) >= MIN_TEXT_CONTRAST) {
        return hexColor.trim();
    }
    let current = rgb;
    for (let step = 0; step < 24; step++) {
        current = { r: current.r * 0.9, g: current.g * 0.9, b: current.b * 0.9 };
        if (contrastRatio(relativeLuminance(current), whiteL) >= MIN_TEXT_CONTRAST) {
            return rgbToHex(current);
        }
    }
    return BRANDING_DARK_INK;
}
/** `"r, g, b"` — the form an `rgba(var(--x), alpha)` CSS rule needs, so a
 *  focus ring or shadow can carry the brand colour at partial opacity. */
export function hexToRgbTriplet(hexColor) {
    if (!hexColor)
        return undefined;
    const rgb = hexToRgb(hexColor);
    if (!rgb)
        return undefined;
    return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
}
//# sourceMappingURL=branding.js.map